import {
  Button,
  Checkbox,
  Container,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  OutlinedInput,
  Paper,
  Stack,
  TextField,
} from "@mui/material";
import React from "react";
import Logo from "../../components/Logo";
import "./login.css";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { selectLabels } from "../../slices/language";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import LoadingModal from "../../components/Modals/LoadingModal";
import api, { LoginPayload } from "../../api";
import { setUser } from "../../slices/user";
import { setToken } from "../../slices/token";
import { useNavigate } from "react-router-dom";
import { showError } from "../../utils/helpers";
import { Context } from "../../context/ContextProvider/ContextProvider";

interface LoginProps {
  isMobile: boolean;
}

const Login = ({ isMobile }: LoginProps) => {
  const labels = useAppSelector(selectLabels);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { setShowDesktopMenu } = React.useContext(Context);

  const [email, setEmail] = React.useState<string>("");
  const [password, setPassword] = React.useState<string>("");
  const [rememberPassword, setRememberPassword] =
    React.useState<boolean>(false);
  const [showPassword, setShowPassword] = React.useState<boolean>(false);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);

  React.useEffect(() => {
    const savedToken = localStorage.getItem("token");
    const savedUser = localStorage.getItem("user");
    let user = null;
    if (savedUser) {
      dispatch(setUser(JSON.parse(savedUser)));
      user = JSON.parse(savedUser);
    }
    if (savedToken) {
      dispatch(setToken(savedToken));
    }
    if (savedToken && savedUser) {
      if (user?.role?.name.startsWith("ciso")) {
        navigate("/properties");
      } else if (user?.role?.name.startsWith("security_guard")) {
        navigate("/logs");
      } else {
        navigate("/access-keys");
      }
    } else {
      navigate("/login");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    const savedEmail = localStorage.getItem("user_email") || null;
    const savedPassword = localStorage.getItem("password") || null;
    if (savedEmail && savedPassword) {
      setPassword(savedPassword);
      setEmail(savedEmail);
    }
  }, []);

  const handleChangeRememberPassword = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRememberPassword(event.target.checked);
  };

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.preventDefault();
  };

  const onSubmit = async () => {
    if (email && password) {
      const adjustedEmail = email.trim().toLowerCase();
      const payload: LoginPayload = {
        identifier: email,
        password,
      };
      try {
        setIsLoading(true);
        if (rememberPassword) {
          localStorage.setItem("user_email", adjustedEmail);
          localStorage.setItem("password", password);
        }
        const response = await api.loginRequest(payload);
        if (response) {
          dispatch(setToken(response?.jwt));
          getUserInfo(response?.user?.id, response?.jwt);
        }
      } catch (error: any) {
        console.error(error);
        if (
          error?.response?.data?.error?.message ===
          "Invalid identifier or password"
        ) {
          showError(dispatch, labels.invalid_credentials);
        } else if (
          error?.response?.data?.error?.message ===
          "Your account email is not confirmed"
        ) {
          showError(dispatch, labels.not_confirmed);
        } else if (
          error?.response?.data?.error?.message ===
          "Your account has been blocked by an administrator"
        ) {
          showError(dispatch, labels.blocked_account);
        } else {
          showError(dispatch, labels.generic_error);
        }
      } finally {
        setIsLoading(false);
      }
    }
  };

  const getUserInfo = async (userId: number, token: string) => {
    if (userId && token) {
      try {
        setIsLoading(true);
        const response = await api.userByIdRequest(userId, token);
        if (response) {
          dispatch(setUser(response));
          if (response?.role?.name.startsWith("ciso")) {
            setShowDesktopMenu(true);
            navigate("/properties");
          } else if (response?.role?.name.startsWith("security_guard")) {
            setShowDesktopMenu(false);
            navigate("/logs");
          } else {
            setShowDesktopMenu(true);
            navigate("/access-keys");
          }
        }
      } catch (error) {
        console.log(error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <Container
      maxWidth="xs"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
        paddingY: "10px",
        boxSizing: "border-box",
        overflow: "hidden",
      }}
    >
      <LoadingModal open={isLoading} setOpen={setIsLoading} />
      <Paper
        variant="outlined"
        className="w-100 p-10 flex flex-dir-col justify-content-center align-items-center border-box"
      >
        <div
          className="w-100 flex flex-dir-col align-items-center justify-content-center gap-6"
          style={{
            paddingTop: 40,
            paddingBottom: 40,
          }}
        >
          <Logo type={"passTrackLogo"} alt="passtrack" className="logo" />
          <p className="main-title mt-8">{labels.log_in}</p>
          <div className="w-100 mt-8">
            <p className="body-text-sm mb-3">{labels.email} *</p>
            <TextField
              fullWidth
              id="email"
              placeholder={"email@example.com"}
              variant="outlined"
              value={email}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setEmail(event.target.value);
              }}
            />
          </div>
          <div className="w-100">
            <p className="body-text-sm mb-3">{labels.password} *</p>
            <FormControl fullWidth variant="outlined">
              <OutlinedInput
                id="password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setPassword(event.target.value);
                }}
                placeholder="Password123"
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
              />
            </FormControl>
          </div>
          <Stack
            className="w-100"
            direction={"row"}
            justifyContent={"flex-start"}
          >
            <FormControlLabel
              label={labels.remember_password}
              control={
                <Checkbox
                  checked={rememberPassword}
                  onChange={handleChangeRememberPassword}
                />
              }
            />
          </Stack>
          <Button
            fullWidth
            disableElevation
            size="large"
            variant="contained"
            disabled={!email || !password}
            onClick={onSubmit}
          >
            {labels.log_in}
          </Button>
          <p
            className="link-text mt-12"
            onClick={() => navigate("/reset-password")}
          >
            {labels.forgot_your_password}
          </p>
        </div>
      </Paper>
    </Container>
  );
};

export default Login;
